<script setup lang="ts">
  import { KEY_ENTER, KEY_SPACE } from '@travelata-libs/utils';
  import { computed } from 'vue';
  import { RadioSize } from './Radio.types';
  import { Typo, TypoVariant } from '@/components';

  const TEXT_VARIANT_BY_SIZE = {
    [RadioSize.S]: TypoVariant.BodySRegular,
    [RadioSize.L]: TypoVariant.BodyLRegular,
    [RadioSize.M]: TypoVariant.BodyMRegular,
    [RadioSize.XL]: TypoVariant.BodyXLRegular,
  };

  interface RadioProps {
    modelValue: string | number;
    value: string | number;
    name: string;
    label?: string;
    disabled?: boolean;
    error?: boolean;
    size?: `${RadioSize}`;
  }

  interface RadioEmits {
    (e: 'update:modelValue', value: string | number): void;
  }

  const props = withDefaults(defineProps<RadioProps>(), {
    label: '',
    disabled: false,
    error: false,
    size: RadioSize.M,
  });

  const emit = defineEmits<RadioEmits>();

  const innerValue = computed({
    get() {
      return props.modelValue;
    },
    set(value: string | number) {
      emit('update:modelValue', value);
    },
  });

  const textVariant = computed(() => {
    return TEXT_VARIANT_BY_SIZE[props.size];
  });

  const selectValue = () => {
    innerValue.value = props.value;
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (!props.disabled && [KEY_ENTER, KEY_SPACE].includes(event.key)) {
      event.preventDefault();
      selectValue();
    }
  };
</script>

<script lang="ts">
  export default {
    model: {
      prop: 'modelValue',
      event: 'update:modelValue',
    },
  };
</script>

<template>
  <div>
    <label
      :class="[
        'tl-radio',
        `tl-radio_size-${size}`,
        {
          'tl-radio_checked': innerValue === value,
          'tl-radio_disabled': disabled,
          'tl-radio_error': error,
        },
      ]"
      role="radio"
      :aria-disabled="disabled"
      @keydown="onKeyDown"
    >
      <input
        v-model="innerValue"
        type="radio"
        class="tl-radio__input"
        :value="value"
        :name="name"
        :disabled="disabled"
        tabindex="-1"
        @click.stop
      />
      <span class="tl-radio__frame" :tabindex="disabled ? -1 : 0" />

      <span v-if="$slots.default || label" class="tl-radio__content">
        <slot>
          <Typo :variant="textVariant" class="tl-radio__label">{{ label }}</Typo>
        </slot>
      </span>
    </label>
  </div>
</template>

<style lang="scss" scoped>
  .tl-radio {
    $root: &;

    align-items: center;
    cursor: pointer;
    display: inline-flex;
    gap: var(--spacing-xs);
    transition: border-color 0.2s, background-color 0.2s, color 0.2s;
    user-select: none;

    @include disable-tap-highlight;

    &_error {
      #{$root}__frame {
        background-color: var(--color-red-025);
        border-color: var(--color-red-60);
      }
    }

    &_disabled {
      cursor: default;

      #{$root}__frame {
        background-color: var(--color-neutral-10);
        border-color: var(--color-text-tertiary);
      }

      #{$root}__content {
        color: var(--color-text-tertiary);
      }

      &#{$root}_checked {
        #{$root}__frame {
          background: var(--color-bg-primary);
          border-color: var(--color-text-tertiary);

          &::after {
            background: var(--color-text-tertiary);
          }
        }
      }
    }

    &_checked {
      #{$root}__frame {
        border-color: var(--color-sky-50);

        &::after {
          background: var(--color-sky-50);
        }
      }
    }

    &_size {
      &-s,
      &-m {
        #{$root}__frame {
          height: 16px;
          width: 16px;
        }
      }

      &-l,
      &-xl {
        #{$root}__frame {
          height: 20px;
          width: 20px;
        }
      }

      &-s {
        #{$root}__content {
          @include typography-body(s, 400);
        }
      }

      &-m {
        #{$root}__content {
          @include typography-body(m, 400);
        }
      }

      &-l {
        #{$root}__content {
          @include typography-body(l, 400);
        }
      }

      &-xl {
        #{$root}__content {
          @include typography-body(xl, 400);
        }
      }
    }

    &__input {
      height: 0;
      margin: 0;
      opacity: 0;
      outline: none;
      padding: 0;
      position: absolute;
      width: 0;
      z-index: -1;
    }

    &__frame {
      background-color: var(--color-bg-primary);
      border: 1px solid var(--color-text-secondary);
      border-radius: 100%;
      box-sizing: border-box;
      display: flex;
      flex-shrink: 0;
      position: relative;

      &::after {
        border-radius: 100%;
        content: '';
        height: 10px;
        left: 50%;
        position: absolute;
        top: 50%;
        transform: translate(-50%, -50%);
        width: 10px;
      }
    }

    &__content {
      align-items: center;
      color: var(--color-text-primary);
      display: flex;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      width: 100%;
    }

    &__label {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      width: 100%;
    }
  }
</style>
